import { useInView } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';

import { SmallProductCard } from '@components/cards';
import Filters from '@components/catalogControls/filters';
import MobileControls from '@components/catalogControls/mobile-controls';
import Sorting from '@components/catalogControls/sorting';
import { Container } from '@components/container';
import { Header } from '@components/header';
import { Navbar } from '@components/navbar';
import { ChevronRight, PackageSearch, TrendingUp } from 'lucide-react';
import Head from 'next/head';

import { getProductCategories, getTrendingProducts } from '@api/product';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink } from '@chakra-ui/react';
import { TProduct, TProductCategory } from '@components/types';
import appConfig from '@core/appconfig';

const SORTING_OPTIONS = [
  { value: 'trending', label: 'Trending' },
  { value: 'relevence', label: 'Relevence' },
  { value: 'popularity', label: 'Popularity' },
  { value: 'rating', label: 'Rating' },
];

export default function TrendingProducts() {
  const [trendingProducts, setTrendingProducts] = useState<TProduct[]>([]);
  const [skip, setSkip] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isData, setIsData] = useState(true);
  const PAGE_LIMIT = 10;
  
  const [allCategories, setAllCategories] = useState<TProductCategory[]>([]);
  const [minimumCategories, setMinimumCategories] = useState<TProductCategory[]>([]);

  const [categoryId, setCategoryId] = useState<null | number>(null);
  const [orderingOn, setOrderingOn] = useState<null | string>(SORTING_OPTIONS[0].value);

  const ref = useRef(null);
  const isInView = useInView(ref);

  function handleCategoryChange(id: number) {
    if (categoryId === id) setCategoryId(null);
    else setCategoryId(id);
    setTrendingProducts([]);
    setIsData(true);
    setSkip(0);
  }

  function handleSortingChange(option: string) {
    if (orderingOn === option) setOrderingOn(null);
    else setOrderingOn(option);
    setTrendingProducts([]);
    setIsData(true);
    setSkip(0);
  }

  function handleClearFilters() {
    setCategoryId(null);
    setOrderingOn(null);
    setTrendingProducts([]);
    setIsData(true);
    setSkip(0);
  }

  async function fetchTrendingProducts() {
    setIsLoading(true);
    try {
      const products: TProduct[] = await getTrendingProducts(
        categoryId,
        orderingOn,
        skip,
        PAGE_LIMIT
      );

      if (skip === 0) setTrendingProducts(products);
      else {
        if (products.length) setTrendingProducts([...trendingProducts, ...products]);
        else setIsData(false);
      }
    } finally {
      setIsLoading(false);
    }
  }

  async function fetchCategories() {
    const categories: TProductCategory[] = await getProductCategories(null, true, 50);
    setAllCategories(categories);
    setMinimumCategories(categories.slice(0, 10))
  }

  useEffect(() => {
    fetchTrendingProducts();
  }, [categoryId, orderingOn, skip]);

  useEffect(() => {
    if (isInView && isData && isLoading === false) {
      setSkip((prevSkip) => prevSkip + PAGE_LIMIT);
      setIsLoading(true);
    }

  }, [isInView, isData, isLoading]);

  useEffect(() => {
    fetchCategories();
  }, []);

  return (
    <Container>
      <Head>
        <title>{`Trending Products - ${appConfig?.appName}`}</title>
        <meta
          name="description"
          content={`Trending Products on Jhattse. Products at best price with available deals and offers on ${appConfig?.appName} from local stores.`}
        />
        <link rel="canonical" href={`https://jhattse.com/product/trending`} />
        <meta
          property="og:image"
          content="https://cdn.jhattse.com/public/consumer/square-logo.png"
        />
        <meta property="og:title" content={`Trending Products on ${appConfig?.appName}`} />
        <meta
          name="og:description"
          content={`Trending Products on Jhattse. Products at best price with available deals and offers on ${appConfig?.appName} from local stores.`}
        />
      </Head>

      <Header />

      <div className="px-2 md:px-8 lg:px-20 font-manrope mx-auto max-w-[1240px] mt-5 pb-6">
        <div className="hidden md:flex items-center pb-4 text-base font-manrope">
          <Breadcrumb className="flex items-center gap-1 text-sm text-neutral-500 font-medium font-manrope list-none">
            <BreadcrumbItem>
              <BreadcrumbLink href="/" className="hover:text-business-500 transition-colors">Home</BreadcrumbLink>
            </BreadcrumbItem>
            <ChevronRight size={14} className="text-neutral-300 mx-1" />
            <BreadcrumbItem
              className="text-ellipsis text-left break-words line-clamp-1 select-none text-business-500 font-semibold"
              isCurrentPage
            >
              <BreadcrumbLink>
                Trending Products
              </BreadcrumbLink>
              <ol className="p-0 list-none"></ol>
            </BreadcrumbItem>
          </Breadcrumb>
        </div>

        <div
          className="relative overflow-hidden rounded-2xl md:rounded-3xl flex items-center min-h-24 md:min-h-32"
          style={{ background: 'linear-gradient(120deg, #0F6E56 0%, #0B5D57 60%, #093F32 100%)' }}
        >
          <div className="relative z-10 flex items-center gap-3 p-4 md:p-6 w-full">
            <div className="flex items-center justify-center w-11 h-11 md:w-14 md:h-14 rounded-2xl bg-white/15 backdrop-blur-sm flex-shrink-0">
              <TrendingUp className="w-5 h-5 md:w-6 md:h-6 text-white" strokeWidth={1.75} />
            </div>
            <div className="flex flex-col gap-0.5 min-w-0">
              <h1 className="text-lg md:text-2xl text-white font-extrabold">
                Trending Products
              </h1>
              <p className="text-xs md:text-sm text-white/70 font-medium">
                What's popular with shoppers right now, near you
              </p>
            </div>
          </div>
        </div>

        <div className="w-full h-fit mt-5 grid grid-cols-1 md:grid-cols-4 bg-white rounded-2xl border border-neutral-200/70 shadow-sm">
          <div className="hidden md:block sticky top-4 self-start max-h-[calc(100vh-5rem)] overflow-y-auto no-scrollbar rounded-l-2xl">
            <Filters
              categoryId={categoryId}
              handleCategoryChange={handleCategoryChange}
              handleClearFilters={handleClearFilters}
              allCategories={allCategories}
              minimumCategories={minimumCategories}
            />
          </div>

          <div className="w-full md:col-span-3">
            <div className="px-2 md:px-6 xl:px-8">
              <Sorting
                options={SORTING_OPTIONS}
                orderingOn={orderingOn}
                handleSortingChange={handleSortingChange}
              />
            </div>

            {/* to control sorting and filtering on smaller devices */}
            <div className="px-2 pt-3">
              <MobileControls
                categoryId={categoryId}
                categories={allCategories}
                handleCategoryChange={handleCategoryChange}
                sortingOptions={SORTING_OPTIONS}
                handleSortingChange={handleSortingChange}
              />
            </div>

            {trendingProducts?.length > 0 && (
              <div className="md:px-6 xl:px-8 py-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
                {trendingProducts?.map((product) => (
                  <SmallProductCard key={product.id} product={product} />
                ))}
              </div>
            )}

            {!isLoading && trendingProducts?.length === 0 && (
              <div className="flex flex-col items-center justify-center gap-2 py-16 px-4">
                <div className="flex items-center justify-center w-16 h-16 rounded-full bg-business-100">
                  <PackageSearch className="w-7 h-7 text-business-500" strokeWidth={1.5} />
                </div>
                <h3 className="text-base font-semibold text-neutral-700 pt-2">No trending products found</h3>
                <p className="text-sm text-neutral-500 text-center">Try a different category or clear your filters.</p>
              </div>
            )}

            {isLoading && (
              <div className="px-12 py-8 flex justify-center">
                <div className="w-7 h-7 rounded-full border-2 border-business-100 border-t-business-500 animate-spin" />
              </div>
            )}

            <div ref={ref} className="w-full h-10" />
          </div>
        </div>
      </div>

      <Navbar />
    </Container>
  );
}
